import * as THREE from 'three';
import { fbm3d } from '../../../visual';

/**
 * Toolbox of organic shape builders. Every flora archetype composes a few
 * of these primitives so the same gentle-curve, no-flat-faces aesthetic
 * is shared across worlds.
 *
 * Geometry rules of thumb:
 * - LatheGeometry with smooth Bézier-sampled cross sections for petals,
 *   bulbs, cups, spires — gives intrinsic curvature for free.
 * - SphereGeometry / IcosahedronGeometry with FBM displacement for blobs,
 *   canopies and bulbs (never raw cubes).
 * - Bent ribbon planes for fronds and blades — smooth high-segment plane,
 *   bezier displacement, double-sided MeshStandardMaterial.
 * - Tube / curved cylinders for stems via QuadraticBezierCurve3.
 *
 * Materials always have `flatShading: false` and roughness ≥ 0.6 — soft,
 * non-plasticky.
 */

export interface BuilderColors {
  readonly tint: number;
  readonly accent: number;
  readonly emissive?: number;
  readonly emissiveStrength?: number;
}

/* ─────────────────────── primitive: bent stem ─────────────────────── */

/** Build a curved stem along a quadratic Bezier from base→top with a
 *  random side bend. Tube radius narrows toward the tip. Returns a Mesh
 *  whose origin is at the base (y=0). */
export function buildStem(
  height: number,
  baseRadius: number,
  tipRadius: number,
  material: THREE.Material,
  bend = 0.18
): THREE.Mesh {
  const a = new THREE.Vector3(0, 0, 0);
  const angle = Math.random() * Math.PI * 2;
  const offset = bend * height;
  const c = new THREE.Vector3(
    Math.cos(angle) * offset,
    height,
    Math.sin(angle) * offset
  );
  const m = new THREE.Vector3(
    Math.cos(angle + Math.PI * 0.4) * offset * 0.4,
    height * 0.55,
    Math.sin(angle + Math.PI * 0.4) * offset * 0.4
  );
  const curve = new THREE.QuadraticBezierCurve3(a, m, c);

  const segments = 18;
  const points = curve.getPoints(segments);
  const radii: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    radii.push(baseRadius * (1 - t) + tipRadius * t);
  }

  const geometry = buildTaperedTube(points, radii, 8);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

function buildTaperedTube(
  points: readonly THREE.Vector3[],
  radii: readonly number[],
  radial: number
): THREE.BufferGeometry {
  const segments = points.length - 1;
  const positions: number[] = [];
  const indices: number[] = [];
  const normals: number[] = [];

  // Compute Frenet-ish frame using up-aligned right vectors.
  const up = new THREE.Vector3(0, 1, 0);
  const tangent = new THREE.Vector3();
  const right = new THREE.Vector3();
  const normal = new THREE.Vector3();

  for (let i = 0; i <= segments; i++) {
    const p = points[i];
    if (!p) continue;
    const next = points[Math.min(i + 1, segments)];
    const prev = points[Math.max(i - 1, 0)];
    if (!next || !prev) continue;
    tangent.copy(next).sub(prev).normalize();
    if (Math.abs(tangent.dot(up)) > 0.95) {
      right.set(1, 0, 0);
    } else {
      right.copy(up).cross(tangent).normalize();
    }
    normal.copy(tangent).cross(right).normalize();

    const r = radii[i] ?? 0;
    for (let j = 0; j < radial; j++) {
      const phi = (j / radial) * Math.PI * 2;
      const cos = Math.cos(phi);
      const sin = Math.sin(phi);
      const dx = right.x * cos + normal.x * sin;
      const dy = right.y * cos + normal.y * sin;
      const dz = right.z * cos + normal.z * sin;
      positions.push(p.x + dx * r, p.y + dy * r, p.z + dz * r);
      normals.push(dx, dy, dz);
    }
  }

  for (let i = 0; i < segments; i++) {
    for (let j = 0; j < radial; j++) {
      const a = i * radial + j;
      const b = i * radial + ((j + 1) % radial);
      const c = (i + 1) * radial + j;
      const d = (i + 1) * radial + ((j + 1) % radial);
      indices.push(a, c, b, b, c, d);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute(normals, 3)
  );
  geometry.setIndex(indices);
  return geometry;
}

/* ─────────────────────── primitive: lathe shapes ─────────────────────── */

/** Lathe profile from a cubic Bezier; result is a smooth curved revolution
 *  ideal for cups, bulbs, bell flowers and pointy spires. */
export function buildLatheFromBezier(
  from: THREE.Vector2,
  cp1: THREE.Vector2,
  cp2: THREE.Vector2,
  to: THREE.Vector2,
  material: THREE.Material,
  segments = 24
): THREE.Mesh {
  const points: THREE.Vector2[] = [];
  const steps = 14;
  const tmp = new THREE.Vector2();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const it = 1 - t;
    const x =
      it * it * it * from.x +
      3 * it * it * t * cp1.x +
      3 * it * t * t * cp2.x +
      t * t * t * to.x;
    const y =
      it * it * it * from.y +
      3 * it * it * t * cp1.y +
      3 * it * t * t * cp2.y +
      t * t * t * to.y;
    tmp.set(Math.max(x, 0.001), y);
    points.push(tmp.clone());
  }
  const geometry = new THREE.LatheGeometry(points, segments);
  geometry.computeVertexNormals();
  return new THREE.Mesh(geometry, material);
}

/** Cup-shaped petal flower head — opens wider at the top with a curled
 *  rim, like a salt bloom. */
export function buildPetalCup(
  height: number,
  radius: number,
  material: THREE.Material
): THREE.Mesh {
  return buildLatheFromBezier(
    new THREE.Vector2(0.05, 0),
    new THREE.Vector2(radius * 0.4, height * 0.05),
    new THREE.Vector2(radius * 0.2, height * 0.6),
    new THREE.Vector2(radius, height),
    material,
    18
  );
}

/** Closed bulb (onion-shape) ideal for vesicle trees, heat bulbs. */
export function buildBulb(
  size: number,
  material: THREE.Material
): THREE.Mesh {
  return buildLatheFromBezier(
    new THREE.Vector2(0.05, 0),
    new THREE.Vector2(size * 1.05, size * 0.15),
    new THREE.Vector2(size * 0.95, size * 0.95),
    new THREE.Vector2(0.05, size * 1.05),
    material,
    20
  );
}

/** Tall narrow spire that flares slightly at the base — used for ferrous
 *  spires and ice spires. */
export function buildSpire(
  height: number,
  radius: number,
  material: THREE.Material
): THREE.Mesh {
  return buildLatheFromBezier(
    new THREE.Vector2(radius * 1.05, 0),
    new THREE.Vector2(radius * 1.3, height * 0.1),
    new THREE.Vector2(radius * 0.45, height * 0.55),
    new THREE.Vector2(0.02, height),
    material,
    14
  );
}

/** Hanging bell / nectar flower — narrow at top, wide opening at bottom. */
export function buildBell(
  height: number,
  radius: number,
  material: THREE.Material
): THREE.Mesh {
  return buildLatheFromBezier(
    new THREE.Vector2(0.02, height),
    new THREE.Vector2(radius * 0.3, height * 0.85),
    new THREE.Vector2(radius * 1.05, height * 0.2),
    new THREE.Vector2(radius * 0.85, 0),
    material,
    18
  );
}

/* ─────────────────────── primitive: blob canopy ─────────────────────── */

/** A "blob" canopy made of an icosphere with FBM noise displacement. The
 *  result is intrinsically lumpy, never boxy, and reads as foliage from
 *  any angle. */
export function buildBlob(
  radius: number,
  material: THREE.Material,
  detail = 2,
  amount = 0.22
): THREE.Mesh {
  const geom = new THREE.IcosahedronGeometry(radius, detail);
  const pos = geom.attributes['position'] as THREE.BufferAttribute;
  const seedX = Math.random() * 60;
  const seedY = Math.random() * 60;
  const seedZ = Math.random() * 60;
  const dir = new THREE.Vector3();
  const stretchY = 0.85 + Math.random() * 0.4;
  for (let i = 0; i < pos.count; i++) {
    dir.set(pos.getX(i), pos.getY(i), pos.getZ(i)).normalize();
    const n = fbm3d(
      dir.x * 1.4 + seedX,
      dir.y * 1.4 + seedY,
      dir.z * 1.4 + seedZ,
      3
    );
    const r = radius * (1 + n * amount);
    pos.setXYZ(i, dir.x * r, dir.y * r * stretchY, dir.z * r);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
  return new THREE.Mesh(geom, material);
}

/* ─────────────────────── primitive: bent frond ─────────────────────── */

/** Curved leaf-blade plane — high segment count along the length, gently
 *  S-curved by displacing the central spine. Returned mesh has its base
 *  at origin, tip pointing up Y. Use scale/rotation to vary the silhouette. */
export function buildFrond(
  length: number,
  width: number,
  material: THREE.Material,
  curl = 0.55
): THREE.Mesh {
  const segments = 22;
  const geom = new THREE.PlaneGeometry(width, length, 4, segments);
  const pos = geom.attributes['position'] as THREE.BufferAttribute;
  const angle = Math.random() * Math.PI * 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    // Map y from [-length/2..length/2] to [0..1] (root→tip).
    const t = (y + length / 2) / length;
    // Spine bend along an arc — quadratic so the tip droops more than the
    // root, like a real frond. Direction picked from `angle` so each frond
    // bends in its own direction.
    const bend = Math.sin(t * Math.PI) * curl * length;
    pos.setX(i, x + cos * bend);
    pos.setZ(i, sin * bend);
    // Taper width toward the tip.
    pos.setX(i, pos.getX(i) * (1 - t * 0.4));
    // Lift the root slightly so the base meets the ground at y=0.
    pos.setY(i, y + length / 2);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(geom, material);
  mesh.castShadow = false;
  return mesh;
}

/** Long thin curved blade — used for glass-blade fans. Same shape as a
 *  frond but narrower and slightly stiffer. */
export function buildBlade(
  length: number,
  width: number,
  material: THREE.Material
): THREE.Mesh {
  return buildFrond(length, width, material, 0.32);
}

/* ─────────────────────── primitive: ground mat ─────────────────────── */

/** Soft mineral / mossy ground patch. A flat-ish PlaneGeometry rotated to
 *  horizontal, with vertex displacement giving it a curved petal-like
 *  silhouette so it doesn't read as a square decal. */
export function buildMat(
  radius: number,
  material: THREE.Material
): THREE.Mesh {
  const geom = new THREE.CircleGeometry(radius, 18);
  geom.rotateX(-Math.PI / 2);
  const pos = geom.attributes['position'] as THREE.BufferAttribute;
  const seedX = Math.random() * 60;
  const seedZ = Math.random() * 60;
  for (let i = 1; i < pos.count; i++) {
    // Skip i=0 (the center vertex) so the patch sits flush at its origin.
    const px = pos.getX(i);
    const pz = pos.getZ(i);
    const n = fbm3d(px * 0.5 + seedX, 0, pz * 0.5 + seedZ, 2);
    const lift = 0.18 * radius * (0.5 + n);
    const inset = 1 + 0.18 * fbm3d(pz * 0.4, 0, px * 0.4, 2);
    pos.setX(i, px * inset);
    pos.setZ(i, pz * inset);
    pos.setY(i, lift);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
  const mesh = new THREE.Mesh(geom, material);
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  return mesh;
}

/* ─────────────────────── primitive: petal ring ─────────────────────── */

/** Ring of petals around a central axis. Each petal is a curved frond
 *  rotated outward and slightly tilted upward, forming a flower head. */
export function buildPetalRing(
  count: number,
  petalLength: number,
  petalWidth: number,
  material: THREE.Material,
  tilt = 0.6
): THREE.Group {
  const group = new THREE.Group();
  for (let i = 0; i < count; i++) {
    const petal = buildFrond(petalLength, petalWidth, material, 0.4);
    petal.rotation.set(
      tilt,
      (i / count) * Math.PI * 2,
      (Math.random() - 0.5) * 0.18
    );
    group.add(petal);
  }
  return group;
}
